import { useEffect, useRef } from 'react';
import { AppBridge, PostMessageTransport } from '@modelcontextprotocol/ext-apps/app-bridge';
import type { ToolOutcome } from '@rally/agent';

/**
 * Renders an MCP App (ui:// resource) the way Alexa+ does on Echo Show: a
 * sandboxed iframe with an opaque origin (srcdoc, no allow-same-origin),
 * a CSP injected from the resource's `_meta.ui.csp`, and the official
 * AppBridge, which proxies the view's tool calls through the host's MCP
 * client. `ui/message` from the view ("Book Lark & Lantern", "Pay for this
 * order") is handed to the assistant as if the customer had said it.
 */

type Props = {
  callTool: (name: string, args: Record<string, unknown>) => Promise<ToolOutcome>;
  readResource: (uri: string) => Promise<{ text: string; meta?: Record<string, unknown> }>;
  resourceUri: string;
  toolName: string;
  data?: Record<string, any>;
  size: 'show8' | 'show15';
  theme: 'dark' | 'light';
  onMessage: (text: string) => void;
  onModelContext?: (text: string) => void;
};

const htmlCache = new Map<string, { text: string; meta?: any }>();

function cspFor(meta: any): string {
  const csp = meta?.ui?.csp ?? {};
  const connect = ['connect-src', ...(csp.connectDomains?.length ? csp.connectDomains : ["'none'"])].join(' ');
  const res = (csp.resourceDomains ?? []).join(' ');
  return [
    "default-src 'none'",
    `script-src 'unsafe-inline' ${res}`.trim(),
    `style-src 'unsafe-inline' ${res}`.trim(),
    `img-src data: blob: ${res}`.trim(),
    `font-src data: ${res}`.trim(),
    connect,
  ].join('; ');
}

export function McpAppFrame({ callTool, readResource, resourceUri, toolName, data, size, theme, onMessage, onModelContext }: Props) {
  const iframeRef = useRef<HTMLIFrameElement>(null);
  const bridgeRef = useRef<AppBridge | null>(null);
  const readyRef = useRef<Promise<void> | null>(null);
  const handlers = useRef({ onMessage, onModelContext, callTool, readResource });
  handlers.current = { onMessage, onModelContext, callTool, readResource };

  // (Re)mount the view when the resource changes.
  useEffect(() => {
    const iframe = iframeRef.current;
    if (!iframe) return;
    let cancelled = false;
    const dims = size === 'show15' ? { width: 1280, maxHeight: 640 } : { width: 960, maxHeight: 460 };
    const bridge = new AppBridge(
      null,
      { name: 'Alexa+ Simulator (Rally)', version: '1.0.0' },
      { openLinks: {}, serverTools: {}, logging: {}, updateModelContext: { text: {} } },
      {
        hostContext: {
          theme,
          platform: 'web',
          displayMode: 'inline',
          availableDisplayModes: ['inline', 'fullscreen'],
          containerDimensions: dims,
          locale: 'en-US',
          deviceCapabilities: { touch: true, hover: false },
          // Alexa+ extension fields (deviceClass is authoritative on Echo devices).
          deviceClass: size === 'show15' ? 'echo-show-15' : 'echo-show-8',
        } as never,
      },
    );
    bridge.oncalltool = async (params) => {
      const out = await handlers.current.callTool(params.name, (params.arguments ?? {}) as Record<string, unknown>);
      return { content: [{ type: 'text', text: out.text || JSON.stringify(out.data ?? {}) }], structuredContent: out.data, isError: out.isError } as never;
    };
    bridge.onmessage = async ({ content }) => {
      const text = (content as Array<{ type: string; text?: string }>).filter((c) => c.type === 'text').map((c) => c.text).join(' ');
      if (text) handlers.current.onMessage(text);
      return {};
    };
    bridge.onupdatemodelcontext = async (params) => {
      const text = ((params as { content?: Array<{ text?: string }> }).content ?? []).map((c) => c.text).join(' ');
      handlers.current.onModelContext?.(text);
      return {};
    };
    bridge.onopenlink = async ({ url }) => {
      window.open(url, '_blank', 'noopener');
      return {};
    };
    bridge.onsizechange = () => {};
    bridgeRef.current = bridge;
    readyRef.current = (async () => {
      let page = htmlCache.get(resourceUri);
      if (!page) {
        const res = await handlers.current.readResource(resourceUri);
        page = { text: res.text, meta: res.meta };
        htmlCache.set(resourceUri, page);
      }
      if (cancelled) return;
      const initialized = new Promise<void>((resolve) => (bridge.oninitialized = () => resolve()));
      await bridge.connect(new PostMessageTransport(iframe.contentWindow!, iframe.contentWindow!));
      const withCsp = page.text.replace('<head>', `<head><meta http-equiv="Content-Security-Policy" content="${cspFor(page.meta)}">`);
      iframe.srcdoc = withCsp;
      await initialized;
      await bridge.sendToolInput({ arguments: {} });
    })();
    return () => {
      cancelled = true;
      void bridge.teardownResource({}).catch(() => {});
      void bridge.close().catch(() => {});
      bridgeRef.current = null;
    };
  }, [resourceUri, size, theme]);

  // Push each new result into the mounted view.
  useEffect(() => {
    if (!data) return;
    const bridge = bridgeRef.current;
    void readyRef.current?.then(() =>
      bridge?.sendToolResult({ content: [{ type: 'text', text: JSON.stringify(data) }], structuredContent: data } as never).catch(() => {}),
    );
  }, [data, resourceUri]);

  return (
    <iframe
      ref={iframeRef}
      className="mcp-app"
      title={`${toolName} view`}
      sandbox="allow-scripts allow-forms"
      referrerPolicy="no-referrer"
    />
  );
}
